import { callExternalApi } from "./external-api.service";
import { getAccessTokenSilentlyOutsideComponent } from "./workspace.service";

const API_SERVER_URL = import.meta.env.VITE_API_SERVER_URL;

export const initializeRecording = async (
  recordingTitle,
  metadata = {},
  language,
) => {
  console.log("Called the initializeRecording method");

  const config = {
    url: `${API_SERVER_URL}/recordings/initialize`,
    method: "POST",
    data: {
      recordingTitle,
      metadata,
      // Left out when not chosen, so the server uses the saved default.
      ...(language ? { language } : {}),
    },
  };
  return await secureApiCall(config);
};

// `signal` (an AbortSignal) cancels the request, e.g. on a timeout.
export const addTranscriptionSegments = async (
  audioFileId,
  segments,
  { signal } = {},
) => {
  console.log("Called the addTranscriptionSegments method");

  const config = {
    url: `${API_SERVER_URL}/recordings/${audioFileId}/transcription/segments`,
    method: "POST",
    data: {
      segments,
    },
    ...(signal ? { signal } : {}),
  };
  return await secureApiCall(config);
};

/**
 * Fetch the current status of a recording.
 *
 * The response always carries the synchronous fields the app already relies on
 * (status, processingStatus, durationSeconds, totalBytes, ...). When the backend
 * `FINALIZE_ASYNC` flag is ON it may ALSO surface the following optional finalize
 * fields (they are null/absent when the flag is OFF, so always decode defensively):
 *   - finalizeStatus: one of QUEUED | COMPOSING | SUBMITTED | COMPLETED |
 *       RETRYABLE_FAILED | TERMINAL_FAILED
 *   - finalizeError: string | null
 *   - finalizeAttempts: number | null
 *
 * A permanent finalize failure surfaces as `finalizeStatus === "TERMINAL_FAILED"`
 * and/or `processingStatus === "ERROR.FINALIZE_FAILED"` (any "ERROR..." prefix).
 */
export const getRecordingStatus = async (audioFileId) => {
  console.log("Called the getRecordingStatus method");

  const config = {
    url: `${API_SERVER_URL}/recordings/${audioFileId}/status`,
    method: "GET",
  };
  // skipCache: live recording/finalize state. A 5-min-cached RECORDING/PAUSED
  // response could make RealTimeRecordingView's post-upload re-check act on
  // stale state (e.g. incorrectly enter cleanup).
  return await secureApiCall(config, true);
};

/**
 * Stop (finalize) a recording.
 *
 * Backward-compatible with two server behaviours, both of which are SUCCESS:
 *   - FINALIZE_ASYNC OFF (default today): HTTP 200 with the full synchronous body
 *     (composed recording, populated assemblyId, etc.).
 *   - FINALIZE_ASYNC ON: HTTP 202 with a minimal in-progress body
 *     `{ audioFileId, audioFileUUID, status: "finalizing", message, assemblyId: null }`.
 *     The recording is composed in the background; the caller should treat this as
 *     in-progress and let the existing processingStatus-driven UI take over.
 *
 * Both 200 and 202 are 2xx, so axios resolves (does not throw) and `secureApiCall`
 * returns `{ data, error: null }`. Callers MUST NOT assume any field beyond what
 * the minimal 202 body provides.
 */
export const stopRecording = async (
  audioFileId,
  finalFileName,
  duration,
  totalBytes,
) => {
  console.log("Called the stopRecording method");

  const config = {
    url: `${API_SERVER_URL}/recordings/${audioFileId}/stop`,
    method: "POST",
    data: {
      finalFileName,
      duration,
      totalBytes,
    },
  };
  return await secureApiCall(config);
};

export const pauseRecording = async (audioFileId) => {
  console.log("Called the pauseRecording method");

  const config = {
    url: `${API_SERVER_URL}/recordings/${audioFileId}/pause`,
    method: "POST",
  };
  return await secureApiCall(config);
};

export const resumeRecording = async (audioFileId) => {
  console.log("Called the resumeRecording method");

  const config = {
    url: `${API_SERVER_URL}/recordings/${audioFileId}/resume`,
    method: "POST",
  };
  return await secureApiCall(config);
};

/**
 * Mint a fresh AssemblyAI realtime streaming token for an in-progress recording.
 * Used to reconnect live transcription (after a pause, or an unexpected socket
 * drop on a long recording) since the original token is short-lived. Returns
 * `{ data: { token }, error }`.
 */
export const getRealtimeToken = async (audioFileId) => {
  console.log("Called the getRealtimeToken method");

  const config = {
    url: `${API_SERVER_URL}/recordings/${audioFileId}/realtime-token`,
    method: "POST",
  };
  return await secureApiCall(config);
};

export const sendRecordingHeartbeat = async (audioFileId, currentDuration) => {
  console.log("Called the sendRecordingHeartbeat method");

  const config = {
    url: `${API_SERVER_URL}/recordings/${audioFileId}/heartbeat`,
    method: "POST",
    data: {
      currentDuration,
    },
  };
  return await secureApiCall(config);
};

export const cleanupActiveRecordings = async () => {
  console.log("Called the cleanupActiveRecordings method");

  const config = {
    url: `${API_SERVER_URL}/recordings/cleanup-active`,
    method: "POST",
  };
  return await secureApiCall(config);
};

export const cleanupStaleRecordings = async () => {
  console.log("Called the cleanupStaleRecordings method");

  const config = {
    url: `${API_SERVER_URL}/recordings/cleanup-stale`,
    method: "POST",
  };
  return await secureApiCall(config);
};

export const getUserRecordingLimits = async () => {
  console.log("Called the getUserRecordingLimits method");

  const config = {
    url: `${API_SERVER_URL}/recordings/limits`,
    method: "GET",
  };
  // skipCache: recording limits are real-time data (daily file count + meter).
  // The default 5-minute GET cache made the RecordingPopup's remaining-files
  // counter show stale values after an upload until a full page refresh.
  return await secureApiCall(config, true);
};

const secureApiCall = async (configParams, skipCache = false) => {
  let accessToken;
  try {
    accessToken = await getAccessTokenSilentlyOutsideComponent();
  } catch (e) {
    if (e.error === "login_required" || e.error === "consent_required") {
      // Handle login redirect if needed
      console.error("Authentication required", e);
    }
    throw e;
  }

  const config = {
    ...configParams,
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  };
  const { data, error, status } = await callExternalApi({ config, skipCache });

  return {
    data: data || null,
    error,
    status,
  };
};
